import { FC, ReactNode, useEffect, useState } from "react";
import useAuthValue, {
  updateAuthValue,
} from "../modules/authModule/hooks/useAuthValue";
import SideDrawerNavigator from "./SideDrawerNavigator";
import { Outlet } from "react-router";
import AppBar from "./AppBar";
import { Box } from "@mui/material";
import Toast from "./Toast";
import { offToast, onToast } from "../utils/ToastEmitter";
import UserMessageBanner from "../modules/userModule/components/UserMessageBanner";
import useGetNewUserCountQuery from "../modules/userModule/hooks/useGetNewUserCountQuery";
import { USER_PERMISSIONS, USER_ROLES } from "../modules/userModule/types/CommonTypes";
import { UseQueryOptions } from "@tanstack/react-query";
import { NEW_USER_COUNT_RESPONSE } from "../modules/userModule/types/ResponseTypes";
import useGetUserDetailQuery from "../modules/userModule/hooks/useGetUserDetailQuery";
import { updateCaseFilterValue } from "../modules/caseManagementModule/hooks/useCaseFilterValue";

interface LayoutProps {
  children?: ReactNode;
}

const Layout: FC<LayoutProps> = ({ children }) => {
  const { userId, tenantId, hospitalId, user, isLoggedIn } = useAuthValue();
 
  const [toast, setToast] = useState<{
    message: string;
    info?: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const isOTManagerAccess = !!user?.permission?.includes(
    USER_PERMISSIONS.OT_MANAGER
  );

  // Resolves the user's role as early as possible after login (rather than
  // waiting on whichever page happens to mount first) so the sidebar never
  // renders the unfiltered menu before role-based filtering can apply.
  const { data: accountDetails, isFetched: isUserDetailFetched } =
    useGetUserDetailQuery({
      userId: userId?.toString(),
      tenantId,
      hospitalId,
    });
  const isUserResolved = !!user || isUserDetailFetched;

  useEffect(() => {
    if (accountDetails) {
      updateAuthValue({ user: accountDetails });
      if (accountDetails.role === USER_ROLES.SURGEON) {
        updateCaseFilterValue({});
      }
    }
  }, [accountDetails]);

  const { data } = useGetNewUserCountQuery({ tenantId, hospitalId }, {
    enabled: isOTManagerAccess && isLoggedIn && !!tenantId && !!hospitalId,
    refetchInterval: 5000, // 5 seconds
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data stale to refetch
  } as UseQueryOptions<NEW_USER_COUNT_RESPONSE>);
  const newUserCountData = (data ?? {}) as NEW_USER_COUNT_RESPONSE;

  useEffect(() => {
    const handleToast = (data: {
      message: string;
      info?: string;
      type: "success" | "error" | "info";
    }) => {
      setToast(data);
      setTimeout(() => setToast(null), 8000);
    };

    onToast(handleToast);

    return () => {
      offToast();
    };
  }, []);

  return (
    <Box height="100vh" display="flex" flexDirection="column" flex={1}>
      <AppBar />

      <Box
        height="100%"
        display="flex"
        flexDirection="row"
        bgcolor="custom.background.secondary"
      >
        {isLoggedIn && isUserResolved && <SideDrawerNavigator />}

        <Box
          position="relative"
          mt={8}
          display="flex"
          flexDirection="column"
          flex={1}
          sx={{ overflowY: "auto" }}
        >
          {isLoggedIn && (
            <UserMessageBanner
              showBanner={!!newUserCountData.showBanner}
              message={newUserCountData.message || ""}
              tenantId={tenantId}
              hospitalId={hospitalId}
            />
          )}
          {toast && (
            <Toast
              message={toast.message}
              info={toast.info}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
          {children ?? <Outlet />}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
